import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "../../lib/supabase.js";
import { useAuth } from "../../lib/auth.js";
import { formatAuPhoneForDisplay, normalizeAuPhone } from "../../lib/phone.js";
import { Button, Field, Input } from "../../lib/ui.js";
import { SectionCard, errorStyle } from "./common.js";

interface JobsSettings {
  jobs_enabled: boolean;
  jobs_alerts_enabled: boolean;
  jobs_alert_mobile: string | null;
}

/**
 * The switch for Leads and jobs (migration 0085) and the owner's text alert for urgent leads (0087). Trades businesses (roofing and the
 * like) start with Jobs on, everyone else off. While it is on, the receptionist's callback requests become jobs and a Jobs page appears in the
 * menu. Alerts are off until the owner turns them on, and only send once the developer has switched text messages on for the business.
 */
export function JobsSection() {
  const { activeBusinessId } = useAuth();
  const [settings, setSettings] = useState<JobsSettings | null>(null);
  const [textsAvailable, setTextsAvailable] = useState(false);
  const [alertsOn, setAlertsOn] = useState(false);
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alertError, setAlertError] = useState<string | null>(null);
  const [alertSaved, setAlertSaved] = useState(false);

  useEffect(() => {
    if (!activeBusinessId) return;
    void supabase
      .from("businesses")
      .select("jobs_enabled, jobs_alerts_enabled, jobs_alert_mobile")
      .eq("id", activeBusinessId)
      .single()
      .then(({ data }: { data: JobsSettings | null }) => {
        setSettings(data);
        if (data) {
          setAlertsOn(data.jobs_alerts_enabled === true);
          setMobile(data.jobs_alert_mobile ? formatAuPhoneForDisplay(data.jobs_alert_mobile) : "");
        }
      });
    void supabase.rpc("rpc_portal_get_sms_settings", { p_business_id: activeBusinessId }).then(({ data }: { data: unknown }) => {
      const row = (Array.isArray(data) ? data[0] : data) as { available?: boolean } | null;
      setTextsAvailable(row?.available === true);
    });
  }, [activeBusinessId]);

  if (settings === null) return null;
  const on = settings.jobs_enabled === true;

  async function change(next: boolean) {
    if (!activeBusinessId) return;
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.rpc("rpc_portal_set_jobs_enabled", { p_business_id: activeBusinessId, p_on: next });
    setBusy(false);
    if (err) return setError(err.message);
    setSettings((s) => (s ? { ...s, jobs_enabled: next } : s));
  }

  async function saveAlerts(e: FormEvent) {
    e.preventDefault();
    if (!activeBusinessId) return;
    setAlertError(null);
    setAlertSaved(false);
    let e164 = "";
    if (mobile.trim()) {
      const n = normalizeAuPhone(mobile, null);
      if (!n.ok || !/^\+614\d{8}$/.test(n.e164)) return setAlertError("Enter an Australian mobile number, such as 0412 345 678.");
      e164 = n.e164;
    }
    if (alertsOn && !e164) return setAlertError("Enter the mobile number to text.");
    setBusy(true);
    const { error: err } = await supabase.rpc("rpc_portal_set_job_alerts", { p_business_id: activeBusinessId, p_on: alertsOn, p_mobile: e164 });
    setBusy(false);
    if (err) return setAlertError(err.message.includes("alert_mobile") ? "Enter an Australian mobile number, such as 0412 345 678." : err.message);
    setAlertSaved(true);
  }

  return (
    <SectionCard
      title="Leads and jobs"
      description="Keep every enquiry the receptionist takes as a job you can follow from the first call to a finished job. Callback requests become jobs automatically, and you can add jobs yourself."
    >
      <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: busy ? "default" : "pointer" }}>
        <input type="checkbox" checked={on} disabled={busy} onChange={(e) => void change(e.target.checked)} style={{ marginTop: 3 }} />
        <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--rc-text)" }}>Use leads and jobs</span>
          <span style={{ fontSize: 12, color: "var(--rc-text-muted)" }}>Adds a Jobs page to the menu. Turning it off keeps your jobs; new callbacks just stop becoming jobs.</span>
        </span>
      </label>
      {error && <p style={errorStyle}>{error}</p>}

      {on && (
        <form onSubmit={saveAlerts} style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10, maxWidth: 520 }}>
          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={alertsOn}
              onChange={(e) => {
                setAlertsOn(e.target.checked);
                setAlertSaved(false);
              }}
              style={{ marginTop: 3 }}
            />
            <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--rc-text)" }}>Text me about urgent leads</span>
              <span style={{ fontSize: 12, color: "var(--rc-text-muted)" }}>
                When a job comes in marked urgent or emergency, a text goes to this mobile with what it is, where and who to ring.
              </span>
            </span>
          </label>
          <Field label="Mobile to text">
            <Input
              value={mobile}
              maxLength={20}
              inputMode="tel"
              placeholder="0412 345 678"
              onChange={(e) => {
                setMobile(e.target.value);
                setAlertSaved(false);
              }}
              style={{ width: 200 }}
            />
          </Field>
          {!textsAvailable && (
            <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)", margin: 0 }}>
              Text messages are not switched on for your business yet, so no alert will be sent until your developer turns them on.
            </p>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Button type="submit" variant="primary" disabled={busy}>
              Save alerts
            </Button>
            {alertSaved && <span style={{ fontSize: 12.5, color: "var(--rc-success)" }}>Saved.</span>}
          </div>
          {alertError && <p style={{ ...errorStyle, margin: 0 }}>{alertError}</p>}
        </form>
      )}
    </SectionCard>
  );
}
